import { useInfiniteQuery } from '@northladder/services';

import { INV_MGMT_SERVICE_API } from '../../../InventoryManagementClient';
import { IGetInventoryInfoPayload } from '../../../types';

import { formatInboundInventoryListResponse } from '../transformers';

/**
 * This is to track the Inbound details from the backend.
 */
export const INBOUND_INVENTORY_INFO_KEY = 'inbound-inventory-info-query';

interface IUseInboundInventoryInfoQuery {
  isQueryEnabled?: boolean;
  payload: IGetInventoryInfoPayload;
}

/**
 * This hook fetches the inbound Pod Details from the backend.
 */
export const useInboundInventoryInfoQuery = ({
  isQueryEnabled = true,
  payload,
}: IUseInboundInventoryInfoQuery) => {
  const { limit, inventoryTransactionId, recordType, searchText } = payload;
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    isSuccess,
    refetch,
  } = useInfiniteQuery({
    enabled: isQueryEnabled,
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      INBOUND_INVENTORY_INFO_KEY,
      limit,
      inventoryTransactionId,
      recordType,
      searchText,
    ],
    initialPageParam: 1,
    networkMode: 'always',
    retry: false,
    queryFn: ({ pageParam = 1 }) =>
      INV_MGMT_SERVICE_API.getInventoryInfo({ ...payload, page: pageParam }),
    getNextPageParam: (lastPage, allPages) => {
      const nextPage = allPages.length + 1;
      const currentCount = allPages.length * limit;

      return currentCount < lastPage.inboundInventories.total
        ? nextPage
        : undefined;
    },
    select: formatInboundInventoryListResponse,
  });

  return {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isPending,
    isSuccess,
    refetch,
  };
};
